/**
 * System Status Summary Card
 * Shows quick overview of service health and key metrics on Dashboard
 */

import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@jourfx/ui";
import {
  Activity,
  ChevronRight,
  Users,
  Video,
  Calendar,
} from "lucide-react";
import { api } from "@jourfx/convex/client";

type ServiceStatus = "operational" | "degraded" | "down";

interface ServiceInfo {
  id: string;
  name: string;
  status: ServiceStatus;
  responseTime?: number;
  lastChecked: number | null;
  errorMessage?: string;
}

interface ServiceIndicatorProps {
  name: string;
  status: ServiceStatus;
}

function getStatusLabel(
  t: (key: string) => string,
  status: ServiceStatus
): string {
  switch (status) {
    case "operational":
      return t("status.operational");
    case "degraded":
      return t("status.degraded");
    case "down":
      return t("status.down");
    default:
      return status;
  }
}

function ServiceIndicator({ name, status }: ServiceIndicatorProps) {
  const { t } = useTranslation("system");

  const statusColors: Record<ServiceStatus, string> = {
    operational: "bg-success",
    degraded: "bg-warning",
    down: "bg-error",
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`h-3 w-3 rounded-full ${statusColors[status]}`}
        title={getStatusLabel(t, status)}
      />
      <span className="text-xs text-muted-foreground truncate max-w-[80px]">
        {name}
      </span>
    </div>
  );
}

interface MetricProps {
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  label: string;
}

function Metric({ icon: Icon, value, label }: MetricProps) {
  return (
    <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-muted/50">
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="text-xl font-bold tabular-nums">{value}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

export function SystemStatusCard() {
  const { t } = useTranslation("system");
  const status = useQuery(api.admin.systemHealth.getStatus);

  if (status === undefined) {
    return <SystemStatusCardSkeleton />;
  }

  if (status === null) {
    return null; // Not authenticated
  }

  const operationalCount = status.services.filter(
    (s: ServiceInfo) => s.status === "operational"
  ).length;
  const totalServices = status.services.length;
  const allOperational = operationalCount === totalServices;

  return (
    <Card className="transition-all hover:shadow-md hover:border-primary/20">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base">
              {t("card.title", "System Status")}
            </CardTitle>
          </div>
          <Link
            to="/system"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {t("card.viewAll", "View All")}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Service Status Indicators */}
        <div className="flex items-center justify-between px-2">
          {status.services.map((service: ServiceInfo) => (
            <ServiceIndicator
              key={service.id}
              name={service.name}
              status={service.status}
            />
          ))}
        </div>

        {/* Overall Status */}
        <div
          className={`text-center py-2 px-3 rounded-lg text-sm font-medium ${
            allOperational
              ? "bg-success-muted text-success-muted-foreground"
              : "bg-warning-muted text-warning-muted-foreground"
          }`}
        >
          {allOperational
            ? t("card.allOperational", "All systems operational")
            : t("card.someIssues", "{{count}}/{{total}} services operational", {
                count: operationalCount,
                total: totalServices,
              })}
        </div>

        {/* Quick Metrics */}
        <div className="grid grid-cols-3 gap-2">
          <Metric
            icon={Users}
            value={status.activity.activeUsers}
            label={t("card.activeUsers", "Active Users")}
          />
          <Metric
            icon={Video}
            value={status.activity.activeVideoSessions}
            label={t("card.videoCalls", "Video Calls")}
          />
          <Metric
            icon={Calendar}
            value={status.activity.appointmentsToday}
            label={t("card.appointmentsToday", "Today")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SystemStatusCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between px-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-3 w-12" />
            </div>
          ))}
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        <div className="grid grid-cols-3 gap-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
